import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Card,
	Collapse,
	Grid,
	LinearProgress,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import {alpha} from "@mui/system/colorManipulator";
import {
	BreadcrumbItem,
	BreadcrumbLink,
	Breadcrumbs,
	Checkbox,
	defined,
	ErrorAlert,
	View,
	ViewHeader,
} from "@variocube/app-ui";
import React, {FormEvent, useMemo, useState} from "react";
import {useAsync, useAsyncCallback} from "react-async-hook";
import {useDropzone} from "react-dropzone";
import {Link as RouterLink} from "react-router-dom";
import {useInvoicesApi} from "../../../../api";
import {useFileApi} from "../../../../api/files";
import {FilePreview} from "../../../../component/file-preview";
import {UnsupportedFileTypeAlert} from "../../../../component/unsupported-file-type-alert";
import {useExchangeRate, useReloadInvoiceCount, useSetting, useUser} from "../../../../context/auth";
import {useLocalization} from "../../../../i18n";
import {notNull} from "../../../../util";
import {isValidAttachmentType, isValidDocumentType} from "../../../../utils/isValidFileType";
import {InvoiceDuplicates} from "../duplicates";
import {ExpenseInvoiceFile, FileUpload} from "../types";
import {detectQrCode} from "./detect-qr-code";
import {AmountStep} from "./steps/amount-step";
import {BusinessCaseStep} from "./steps/business-case-step";
import {CarStep} from "./steps/car-step";
import {CreditCardStep} from "./steps/credit-card-step";
import {DetailStep} from "./steps/detail-step";
import {InputTaxDeductibleStep} from "./steps/input-tax-deductible-step";
import {JustificationStep} from "./steps/justification-step";
import {ParticipantStep} from "./steps/participant-step";
import {RecipientStep} from "./steps/recipient-step";
import {SubmitOnBehalfStep} from "./steps/submit-on-behalf-step";
import {SubmitExpenseInvoiceInfo} from "./submit-expense-invoice-info";
import {SubmitExpenseInvoiceSuccess} from "./submit-expense-invoice-success";

enum Steps {
	UPLOAD,
	SUBMIT_ON_BEHALF,
	CREDIT_CARD,
	BUSINESS_CASE,
	RECIPIENT,
	DETAIL,
	PARTICIPANT,
	INPUT_TAX_DEDUCTIBLE,
	CAR,
	AMOUNT,
	JUSTIFICATION,
	SUMMARY,
}

const USE_INBOX_FILE_TYPES = false;

interface ExpenseInvoiceCreateProps {
	type: "expense" | "credit-card";
}

export function ExpenseInvoiceCreate({type}: Readonly<ExpenseInvoiceCreateProps>) {
	const {t} = useLocalization();
	const {uploadFile} = useFileApi();
	const {submitExpenseInvoice} = useInvoicesApi();
	const reloadInvoiceCount = useReloadInvoiceCount();

	const qrCodeEnabled = useSetting("qrCode");
	const user = useUser();
	const canRepresent = user.represents.length > 0;

	const creditCard = type == "credit-card";

	const [invoice, setInvoice] = useState<ExpenseInvoiceFile>({
		files: [],
	});
	const [stepIndex, setStepIndex] = useState(0);

	const exchangeRate = useExchangeRate(invoice.currency ?? "EUR");

	function updateInvoice(update: Partial<ExpenseInvoiceFile>) {
		setInvoice(prev => validateFileTypes({...prev, ...update}));
	}

	function updateParticipantFile(file: File, update: Omit<FileUpload, "file">) {
		setInvoice(prev => validateFileTypes({...prev, participantFile: {...prev.participantFile, ...update, file}}));
	}

	function updateInvoiceFile(file: File, update: Omit<FileUpload, "file">) {
		setInvoice(prev =>
			validateFileTypes({...prev, files: prev.files.map(f => f.file === file ? {...f, ...update} : f)})
		);
	}

	function uploadInvoice(files: File[]) {
		if (step != Steps.PARTICIPANT) {
			// if the user is not on the participant step, this is an invoice file
			updateInvoice({
				files: [...invoice.files, ...files.map(f => ({file: f, progress: 0, error: undefined}))],
			});
			for (const file of files) {
				uploadFile(file, {
					onProgress: progress => updateInvoiceFile(file, {progress}),
					onSuccess: tempFile => updateInvoiceFile(file, {...tempFile}),
					onError: error => updateInvoiceFile(file, {error}),
				});
			}
			if (step == Steps.UPLOAD) {
				navigate(1);
			}
		}
		else {
			// user is on the participant step, so this is a participant file
			const [file] = files;
			updateInvoice({participantFile: {file}});
			uploadFile(file, {
				onProgress: progress => updateParticipantFile(file, {progress}),
				onSuccess: tempFile => updateParticipantFile(file, {...tempFile}),
				onError: error => updateParticipantFile(file, {error}),
			});
		}
	}

	const {getRootProps, getInputProps, isDragActive, open} = useDropzone({
		useFsAccessApi: false,
		multiple: true,
		onDrop: uploadInvoice,
		noClick: true,
	});

	const theme = useTheme();

	// auto detect QR-code
	useAsync(async (files: FileUpload[], qrCodeEnabled: boolean | undefined) => {
		const file = files[0]?.file;
		if (file && qrCodeEnabled) {
			console.info("Trying to detect QR-code");
			const qrCode = await detectQrCode(file);
			if (qrCode) {
				console.info("Detected QR-code");
				updateInvoice({qrCode});
			}
			else {
				console.info("Could not detect QR-code");
			}
		}
		return Promise.resolve();
	}, [invoice.files, qrCodeEnabled]);

	const unsupportedFileType = invoice.files.some(upload => defined(upload.supported) && !upload.supported)
		|| (defined(invoice.participantFile?.supported) && !invoice.participantFile?.supported);

	const total = invoice?.businessCases?.map(businessCase => businessCase.amount).filter(defined).filter(notNull)
		.reduce((a, b) => a + b, 0);

	const totalEur = defined(total) && defined(exchangeRate) ? total / exchangeRate : 0;

	const aboveComplianceThreshold = defined(totalEur) && totalEur > 2000;
	const aboveUsualThreshold = defined(totalEur) && totalEur > 400;
	const notAboveZero = defined(total) && total <= 0;

	const isCatering = invoice?.businessCaseCategory == "Catering"
		|| invoice?.businessCaseCategory == "ExternalCatering";

	const isJustification = invoice?.businessCases?.some(businessCase => businessCase.emergency)
		|| (aboveUsualThreshold && !aboveComplianceThreshold);

	const isCar = invoice?.businessCaseCategory == "Car";

	const hasCarBusinessCase = invoice?.businessCases?.some(bc => bc.car) ?? false;

	const requiresCarData = isCar || (hasCarBusinessCase && invoice.inputTaxDeductible === true);

	const steps = useMemo(() => {
		const steps = [Steps.UPLOAD];
		if (canRepresent) {
			steps.push(Steps.SUBMIT_ON_BEHALF);
		}
		if (creditCard) {
			steps.push(Steps.CREDIT_CARD);
		}
		steps.push(Steps.BUSINESS_CASE, Steps.RECIPIENT, Steps.DETAIL);
		if (isCatering) {
			steps.push(Steps.PARTICIPANT);
		}
		if (hasCarBusinessCase) {
			steps.push(Steps.INPUT_TAX_DEDUCTIBLE);
		}
		if (requiresCarData) {
			steps.push(Steps.CAR);
		}
		steps.push(Steps.AMOUNT);
		if (isJustification) {
			steps.push(Steps.JUSTIFICATION);
		}
		steps.push(Steps.SUMMARY);
		return steps;
	}, [canRepresent, creditCard, isCatering, hasCarBusinessCase, requiresCarData, isJustification]);

	const step = useMemo(() => steps[stepIndex], [steps, stepIndex]);

	function navigate(offset: number) {
		setStepIndex(prev => prev + offset);
	}

	const submit = useAsyncCallback(async (e: FormEvent) => {
		e.preventDefault();
		if (step != Steps.SUMMARY) {
			navigate(1);
		}
		else {
			// form validation should ensure that the relevant fields are defined
			const created = await submitExpenseInvoice({
				files: invoice.files.map(upload => ({
					checksum: upload.checksum!,
					filename: upload.filename!,
					contentType: upload.contentType!,
				})),
				inboxId: invoice.inbox!.id,
				auditors: invoice.auditors!,
				countryCode: invoice.countryCode!,
				currency: invoice.currency ?? "EUR",
				tipCurrency: invoice.tipCurrency,
				creditCardNumber: invoice.creditCardNumber,
				numberOfParticipants: invoice.numberOfParticipants,
				participants: invoice.participants,
				participantAttachment: invoice.participantFile && {
					checksum: invoice.participantFile.checksum!,
					filename: invoice.participantFile.filename!,
					contentType: invoice.participantFile.contentType!,
				},
				profitCenter: invoice.profitCenter,
				caseNumber: invoice.caseNumber,
				tripId: invoice.tripId,
				onBehalfOfUserId: invoice.onBehalfOfUserId,
				emergencyJustification: invoice.emergencyJustification,
				inputTaxDeductible: invoice.inputTaxDeductible,
				car: requiresCarData && invoice.car ? {
					licensePlate: invoice.car.licensePlate!,
					carType: invoice.car.carType,
					mileageKm: invoice.car.mileageKm!,
					refuelLiter: invoice.car.refuelLiter ?? undefined,
				} : undefined,
				businessCases: invoice.businessCases!.map(businessCase => ({
					id: businessCase.id,
					description: businessCase.userDescription,
					amount: businessCase.amount!,
					tip: businessCase.tip ?? undefined,
				})),
				cashTip: invoice.cashTip,
			});
			await reloadInvoiceCount();
			return created;
		}
	});

	function handlePrev() {
		navigate(-1);
	}

	const title = t(`invoices.new.expense.title.${creditCard ? "creditCard" : "expense"}`);

	return (
		<Box
			{...getRootProps()}
			flex={1}
			border={`1px solid ${isDragActive ? theme.palette.secondary.main : "transparent"}`}
			bgcolor={isDragActive
				? alpha(theme.palette.secondary.main, theme.palette.action.selectedOpacity)
				: undefined}
		>
			<View>
				<Breadcrumbs>
					<BreadcrumbLink component={RouterLink} to="/invoices">{t("invoices.plural")}</BreadcrumbLink>
					<BreadcrumbItem>{title}</BreadcrumbItem>
				</Breadcrumbs>
				<ViewHeader title={title} />
				<Collapse in={Boolean(submit.result)}>
					<SubmitExpenseInvoiceSuccess created={submit.result ?? []} />
				</Collapse>
				<Collapse in={!submit.result}>
					<Stack spacing={2}>
						<Collapse in={invoice.files.length == 0}>
							<SubmitExpenseInvoiceInfo />
						</Collapse>

						<form>
							<input {...getInputProps()} />
						</form>
						{invoice.files.length == 0 && (
							<Stack spacing={2}>
								<Button
									color="primary"
									variant="outlined"
									onClick={open}
									size="large"
									sx={{height: 240}}
								>
									{t("invoices.new.expense.uploadInvoice")}
								</Button>
							</Stack>
						)}
						{invoice.files.length > 0 && (
							<Box>
								<Grid container spacing={2}>
									<Grid item xs={12} sm={6} md={4}>
										<Stack spacing={2}>
											{invoice.files.map(upload => (
												<Card>
													<FilePreview file={upload.file} />
													<LinearProgress
														variant="determinate"
														value={(upload.progress ?? 0) * 100}
														color={getInvoiceColor(upload)}
													/>
												</Card>
											))}
											<Button color="secondary" variant="outlined" onClick={open}>
												Beilage hinzufügen
											</Button>
											{invoice.participantFile && step > Steps.PARTICIPANT && (
												<Card>
													<FilePreview file={invoice.participantFile.file} />
												</Card>
											)}
										</Stack>
									</Grid>

									<Grid item xs={12} sm={6} md={8}>
										{invoice.files.some(f => f.duplicates?.length) && (
											<Stack spacing={2}>
												{invoice.files.filter(f => f.duplicates?.length).map(f => (
													<InvoiceDuplicates
														key={f.file.name}
														duplicates={f.duplicates!}
														title={t("invoices.new.duplicate.title")}
														description={t("invoices.new.duplicate.description")}
													/>
												))}
												<Button
													color="primary"
													variant="outlined"
													onClick={open}
												>
													Anderen Beleg auswählen
												</Button>
											</Stack>
										)}
										{!invoice.files.some(f => f.duplicates?.length) && (
											<Stack spacing={2} component="form" onSubmit={submit.execute}>
												{canRepresent && (
													<SubmitOnBehalfStep
														active={step == Steps.SUBMIT_ON_BEHALF}
														completed={step > Steps.SUBMIT_ON_BEHALF}
														invoice={invoice}
														onChange={updateInvoice}
													/>
												)}
												{creditCard && (
													<CreditCardStep
														active={step == Steps.CREDIT_CARD}
														completed={step > Steps.CREDIT_CARD}
														invoice={invoice}
														onChange={updateInvoice}
													/>
												)}
												<BusinessCaseStep
													active={step == Steps.BUSINESS_CASE}
													completed={step > Steps.BUSINESS_CASE}
													invoice={invoice}
													onChange={updateInvoice}
												/>
												<RecipientStep
													active={step == Steps.RECIPIENT}
													completed={step > Steps.RECIPIENT}
													invoice={invoice}
													onChange={updateInvoice}
												/>
												<DetailStep
													active={step == Steps.DETAIL}
													completed={step > Steps.DETAIL}
													invoice={invoice}
													onChange={updateInvoice}
												/>
												{isCatering && (
													<ParticipantStep
														active={step == Steps.PARTICIPANT}
														completed={step > Steps.PARTICIPANT}
														invoice={invoice}
														onChange={updateInvoice}
														onSelectParticipantFile={open}
													/>
												)}
												{hasCarBusinessCase && (
													<InputTaxDeductibleStep
														active={step == Steps.INPUT_TAX_DEDUCTIBLE}
														completed={step > Steps.INPUT_TAX_DEDUCTIBLE}
														invoice={invoice}
														onChange={updateInvoice}
													/>
												)}
												{requiresCarData && (
													<CarStep
														active={step == Steps.CAR}
														completed={step > Steps.CAR}
														invoice={invoice}
														onChange={updateInvoice}
														companyCar={isCar}
													/>
												)}
												<AmountStep
													active={step == Steps.AMOUNT}
													completed={step > Steps.AMOUNT}
													invoice={invoice}
													onChange={updateInvoice}
												/>
												{isJustification && (
													<JustificationStep
														active={step == Steps.JUSTIFICATION}
														completed={step > Steps.JUSTIFICATION}
														invoice={invoice}
														onChange={updateInvoice}
														title={aboveUsualThreshold ? "Begründung" : "Gefahr im Verzug"}
													/>
												)}
												<Collapse in={step == Steps.SUMMARY}>
													<Alert severity="info">
														<AlertTitle>Prüfen und Einreichen</AlertTitle>
														<Typography paragraph>
															Bitte überprüfen Sie Ihre Angaben, bestätigen Sie die
															sachliche Richtigkeit des Belegs und reichen Sie den Beleg
															dann ein.
														</Typography>
													</Alert>
												</Collapse>

												<Collapse in={step == Steps.SUMMARY}>
													<Checkbox
														required={step == Steps.SUMMARY}
														label="Ich bestätige die sachliche Richtigkeit des Belegs."
													/>
												</Collapse>

												{invoice.files.some(upload =>
													defined(upload.supported) && !upload.supported
												) && (
													<UnsupportedFileTypeAlert
														supported={invoice.inbox?.assignedInvoiceDocumentTypes ?? []}
													/>
												)}

												{defined(invoice.participantFile?.supported)
													&& !invoice.participantFile?.supported && (
													<UnsupportedFileTypeAlert
														supported={invoice.inbox?.assignedAttachmentDocumentTypes ?? []}
													/>
												)}

												{aboveComplianceThreshold && (
													<Alert severity="error">
														<AlertTitle>Gesamtbetrag zu hoch</AlertTitle>
														Laut Compliance-Abteilung dürfen nur Barrechnungen bis zu einem
														Maximalbetrag von € 2.000,- brutto eingereicht werden.
													</Alert>
												)}

												{aboveUsualThreshold && (
													<Alert severity="warning">
														<AlertTitle>Gesamtbetrag hoch</AlertTitle>
														Barrechnungen über € 400,- dürfen nur in Ausnahmefällen über die
														Bürokassa eingereicht werden. Erläutern Sie dies bitte im
														nächsten Feld "Begründung".
													</Alert>
												)}

												{submit.error && <ErrorAlert error={submit.error} />}

												<Stack direction="row" justifyContent="space-between">
													<Button
														variant="outlined"
														color="inherit"
														onClick={handlePrev}
														disabled={stepIndex <= 1}
													>
														{t("actions.back")}
													</Button>
													<Button
														type="submit"
														variant="contained"
														color="primary"
														disabled={submit.loading
															|| unsupportedFileType
															|| aboveComplianceThreshold
															|| (step == Steps.SUMMARY && notAboveZero)
															|| invoice.files.some(f => f.duplicates?.length)
															|| !invoice.files.every(f => f.checksum)}
													>
														{step == Steps.SUMMARY
															? title
															: t("actions.next")}
													</Button>
												</Stack>
											</Stack>
										)}
									</Grid>
								</Grid>
							</Box>
						)}
					</Stack>
				</Collapse>
			</View>
		</Box>
	);
}

function getInvoiceColor(invoice: FileUpload) {
	if (
		invoice.error
		|| invoice.duplicates
		|| (defined(invoice.supported) && !invoice.supported)
	) {
		return "error";
	}
	if (invoice.checksum) {
		return "success";
	}
	return "secondary";
}

function validateFileTypes(invoice: ExpenseInvoiceFile): ExpenseInvoiceFile {
	return {
		...invoice,
		files: invoice.files.map(fileUpload => ({
			...fileUpload,
			supported: USE_INBOX_FILE_TYPES
				? isValidDocumentType(invoice.inbox ?? undefined, fileUpload.contentType)
				: isValidExpenseFileType(fileUpload.contentType),
		})),
		participantFile: invoice.participantFile
			? {
				...invoice.participantFile,
				supported: USE_INBOX_FILE_TYPES
					? isValidAttachmentType(invoice.inbox ?? undefined, invoice.participantFile.file?.type)
					: isValidExpenseFileType(invoice.participantFile.file?.type),
			}
			: undefined,
	};
}

function isValidExpenseFileType(contentType?: string) {
	if (contentType) {
		return (contentType.startsWith("application/") && contentType.endsWith("pdf"))
			|| (contentType.startsWith("image/")
				&& (contentType.endsWith("jpeg") || contentType.endsWith("gif") || contentType.endsWith("png")));
	}
}
